/**
 * UnifiedReportPage - Single source of truth for inspection reports
 * 
 * This page serves BOTH admin (Preview) and customer (Published) views:
 * - Admin access: JWT authentication, shows edit/publish buttons
 * - Customer access: OTP session authentication, read-only view
 * 
 * BENEFITS:
 * - Guaranteed data parity (same code path for both views)
 * - Single transform function (no more Preview vs Published bugs)
 * - Easier maintenance (one page to update)
 * 
 * Routes:
 * - /report/:inspectionId (admin preview with JWT)
 * - /report/:reportCode (customer view with OTP)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { EditModeProvider } from '@/contexts/EditModeContext';

// Shared report components
import { Header } from '@/components/report/Header';
import { HeroSection } from '@/components/report/HeroSection';
import { AssessmentSummary } from '@/components/report/AssessmentSummary';
import { KeyInfoSection } from '@/components/report/KeyInfoSection';
import { RepairsSection } from '@/components/report/RepairsSection';
import { RTOVerificationSection } from '@/components/report/RTOVerificationSection';
import { OBDReportMobile } from '@/components/report/OBDReportMobile';
import { InspectionDetailsSection } from '@/components/report/InspectionDetailsSection';
import { VehicleDetailsSection } from '@/components/report/VehicleDetailsSection';
import { Footer } from '@/components/report/Footer';
import { ServiceHistorySection } from '@/components/report/ServiceHistorySection';

// UI components
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Loader2, Phone, ArrowLeft, Star, Shield, CheckCircle,
  Fuel, Calendar, MapPin, Gauge, ChevronRight, LogOut,
  FileText, Clock, Eye, Lock, AlertTriangle
} from 'lucide-react';

// Shared transform function - SINGLE SOURCE OF TRUTH
import { transformInspectionToReport } from '@/utils/reportTransforms';
import '@/styles/inspection-report.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/assets/wisedrive-logo.png';

// ─── Loading Skeleton ─────────────────────────────────────
function ReportSkeleton() {
  return (
    <div className="inspection-report min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Error Screen ─────────────────────────────────────────
function ErrorScreen({ error, onRetry, onBack }) {
  return (
    <div className="inspection-report min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-red-600 mb-2">Failed to load report</h2>
        <p className="text-gray-500 mb-4">{error}</p>
        <div className="flex gap-2 justify-center">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          )}
          {onRetry && (
            <Button onClick={onRetry}>
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── OTP Login Screen (Customer Only) ─────────────────────
function OTPLoginScreen({ reportCode, onVerified }) {
  const [step, setStep] = useState('loading');
  // The backend resolves an older portal-code link to that customer's report,
  // and tells us which report it landed on — everything after uses that.
  const [code, setCode] = useState(reportCode);
  const [reportInfo, setReportInfo] = useState(null);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState(null);

  // Fetch report info
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/customer-portal/report/${reportCode}/info`);
        setReportInfo(data);
        setCode(data.report_id || reportCode);
        setStep('phone');
      } catch {
        setError('This report link is invalid or has expired.');
        setStep('error');
      }
    })();
  }, [reportCode]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // One door. The report link used to fall back to the portal-code route when
  // this one answered anything at all, and that route reads a different store —
  // so a locked-out customer was told "OTP not requested" while holding a valid
  // code. Every report login now goes through this endpoint only.
  const sendOtp = async () => {
    if (phone.length < 10) {
      toast.error('Enter a valid 10-digit number');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/customer-portal/report/${code}/request-otp`, { phone });
      toast.success('OTP sent to your phone');
      setStep('otp');
      setCountdown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/customer-portal/report/${code}/verify-otp`, { phone, otp });
      toast.success('Verified successfully!');
      onVerified(data.session_token, data.inspection_id, data.customer_name);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-[#E8F4FC] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-[#E8F4FC] flex items-center justify-center p-4">
        <Card className="border-none shadow-lg max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <img src={LOGO_URL} alt="WiseDrive" className="h-8 mx-auto mb-6" />
            <Lock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8F4FC] flex items-center justify-center p-4">
      <Card className="border-none shadow-lg max-w-sm w-full">
        <CardContent className="p-6">
          <div className="border-t-4 border-[#1e3a5f] -mt-6 -mx-6 mb-6 rounded-t-lg" />
          <img src={LOGO_URL} alt="WiseDrive" className="h-8 mx-auto mb-4" />
          
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FileText className="h-6 w-6 text-[#1e3a5f]" />
            </div>
            <h1 className="text-lg font-bold text-[#1e3a5f]">Vehicle Inspection Report</h1>
            <p className="text-slate-500 text-sm">Verify your phone to view the report</p>
          </div>

          {reportInfo && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
              <p className="font-medium text-slate-700">{reportInfo.vehicle_make} {reportInfo.vehicle_model}</p>
              <p className="text-slate-500 text-xs">{reportInfo.car_number}</p>
            </div>
          )}

          {step === 'phone' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mobile Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-slate-100 rounded-lg text-sm font-medium text-slate-600">
                    +91
                  </div>
                  <Input
                    type="tel"
                    placeholder="Enter 10-digit number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="flex-1"
                    data-testid="otp-phone-input"
                  />
                </div>
              </div>
              <Button
                onClick={sendOtp}
                disabled={loading || phone.length < 10}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                data-testid="send-otp-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                Send OTP
              </Button>
            </div>
          )}

          {step === 'otp' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Enter OTP</label>
                <p className="text-xs text-slate-500 mb-2">Sent to +91 {phone}</p>
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  className="justify-center"
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    {[...Array(6)].map((_, i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                data-testid="verify-otp-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Verify & View Report
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  onClick={() => { setStep('phone'); setOtp(''); }}
                  className="text-slate-500 hover:text-[#1e3a5f]"
                  data-testid="change-number-btn"
                >
                  Change number
                </button>
                <button
                  onClick={sendOtp}
                  disabled={countdown > 0 || loading}
                  className={countdown > 0 ? 'text-slate-400' : 'text-[#1e3a5f] hover:underline'}
                  data-testid="resend-otp-btn"
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}

          <p className="text-[10px] text-slate-400 text-center mt-4">
            Secured by Wisedrive Technologies Private Limited
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Report Content (Shared between Admin & Customer) ─────
function ReportContent({ data, isAdmin, onBack, onRefresh, inspectionId }) {
  return (
    <EditModeProvider>
      <div className="inspection-report min-h-screen">
        {/* Header - Different for admin vs customer */}
        {isAdmin ? (
          <Header
            data={data.header}
            onRefresh={onRefresh}
            inspectionId={inspectionId}
          />
        ) : (
          <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-4 h-14">
              <div className="flex items-center gap-3">
                <button
                  onClick={onBack}
                  className="p-2 -ml-2 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="report-back-btn"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </button>
                <img src={LOGO_URL} alt="WiseDrive" className="h-6" data-testid="report-logo" />
              </div>
              <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400">
                <FileText className="h-3.5 w-3.5" />
                {data.header?.inspectionType}
                {data.header?.location && (
                  <>
                    <span>&middot;</span>
                    <MapPin className="h-3.5 w-3.5" />
                    {data.header.location}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Report Body - IDENTICAL for both admin and customer */}
        <main className="max-w-7xl mx-auto pb-8">
          <HeroSection header={data.header} vehicleInfo={data.vehicleInfo} keyInfo={data.keyInfo} />
          <AssessmentSummary data={data.assessmentSummary} />
          <VehicleDetailsSection data={data.vehicleInfo} />
          <KeyInfoSection data={data.keyInfo} inspectionCategories={data.inspectionCategories} />
          <RTOVerificationSection data={data.rtoVerification} />
          <RepairsSection data={data.estimatedRepairs} />
          <OBDReportMobile data={data.obdReport} />
          <InspectionDetailsSection data={data.inspectionCategories} />
          <ServiceHistorySection inspectionId={inspectionId} />
        </main>

        <Footer data={data.footer} />
      </div>
    </EditModeProvider>
  );
}

// ─── Data Fetching Hook (SINGLE SOURCE OF TRUTH) ──────────
function useReportData(inspectionId, sessionToken = null, authToken = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let headers = {};
      let baseEndpoint = '';

      if (authToken) {
        // Admin access - use admin APIs
        headers = { Authorization: `Bearer ${authToken}` };
        baseEndpoint = `${API_URL}/api/inspections/${inspectionId}`;
      } else if (sessionToken) {
        // Customer access - use customer portal APIs
        baseEndpoint = `${API_URL}/api/customer-portal/inspection/${inspectionId}`;
      } else {
        throw new Error('No authentication provided');
      }

      // Fetch all data in parallel - SAME endpoints for both admin and customer
      const [inspectionRes, liveProgressRes, repairsRes] = await Promise.all([
        axios.get(
          authToken ? baseEndpoint : `${baseEndpoint}/report`,
          authToken ? { headers } : { params: { session_token: sessionToken } }
        ),
        axios.get(
          authToken ? `${baseEndpoint}/live-progress` : `${baseEndpoint}/live-progress`,
          authToken ? { headers } : { params: { session_token: sessionToken } }
        ).catch(() => ({ data: {} })),
        axios.get(
          authToken ? `${baseEndpoint}/calculated-repairs` : `${baseEndpoint}/calculated-repairs`,
          authToken ? { headers } : { params: { session_token: sessionToken } }
        ).catch(() => ({ data: { minor_repairs: [], major_repairs: [], total_minor: 0, total_major: 0 } })),
      ]);

      // Extract data based on response structure
      let inspection, lead, customer;
      if (authToken) {
        // Admin API returns inspection directly
        inspection = inspectionRes.data;
        lead = null;
        customer = null;
      } else {
        // Customer portal API returns { inspection, lead, customer }
        inspection = inspectionRes.data.inspection;
        lead = inspectionRes.data.lead;
        customer = inspectionRes.data.customer;
      }

      // SINGLE transform function for both views - GUARANTEED PARITY
      const reportData = transformInspectionToReport(
        inspection,
        lead,
        customer,
        liveProgressRes.data,
        repairsRes.data
      );

      setData(reportData);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [inspectionId, sessionToken, authToken]);

  useEffect(() => {
    if (inspectionId) {
      fetchData();
    }
  }, [inspectionId, fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ─── Main Component ───────────────────────────────────────
export default function UnifiedReportPage() {
  const { id } = useParams(); // Can be inspectionId (UUID) or reportCode (short code)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token: authToken, loading: authLoading } = useAuth();

  // State for customer authentication
  const [sessionToken, setSessionToken] = useState(null);
  const [inspectionId, setInspectionId] = useState(null);
  const [customerName, setCustomerName] = useState(null);

  // Determine if this is admin or customer access based on URL format
  // UUID format = admin preview access (requires login)
  // Short code format = customer portal access (requires OTP)
  const isUUID = id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  const isAdminRoute = isUUID; // If UUID, this is an admin route
  const isAdmin = isAdminRoute && !!authToken; // Admin with valid token

  // For admin, inspectionId is the URL param
  // For customer, we need to get it after OTP verification
  const effectiveInspectionId = isAdmin ? id : inspectionId;

  // Fetch report data using shared hook
  const { data, loading, error, refetch } = useReportData(
    effectiveInspectionId,
    isAdmin ? null : sessionToken,
    isAdmin ? authToken : null
  );

  // Handle OTP verification callback
  const handleVerified = (token, inspId, name) => {
    setSessionToken(token);
    setInspectionId(inspId);
    setCustomerName(name);
  };

  // Navigation handlers
  const handleBack = () => {
    if (isAdmin) {
      navigate(-1);
    } else {
      // Customer - go back to inspection list or login
      setSessionToken(null);
      setInspectionId(null);
    }
  };

  // Show loading while auth context initializes
  if (authLoading) {
    return <ReportSkeleton />;
  }

  // Admin route (UUID) - requires login
  if (isAdminRoute) {
    if (!authToken || !user) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <Card className="border-none shadow-lg max-w-md w-full">
            <CardContent className="p-8 text-center">
              <Lock className="h-12 w-12 text-amber-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
              <p className="text-gray-600 mb-6">Please log in to the CRM to access the report preview.</p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Admin is logged in - show report
    if (loading) return <ReportSkeleton />;
    if (error) return <ErrorScreen error={error} onRetry={refetch} onBack={handleBack} />;
    if (!data) return null;

    return (
      <ReportContent
        data={data}
        isAdmin={true}
        onBack={handleBack}
        onRefresh={refetch}
        inspectionId={effectiveInspectionId}
      />
    );
  }

  // Customer accessing published report
  if (!sessionToken) {
    return <OTPLoginScreen reportCode={id} onVerified={handleVerified} />;
  }

  if (loading) return <ReportSkeleton />;
  if (error) return <ErrorScreen error={error} onRetry={refetch} onBack={handleBack} />;
  if (!data) return null;

  return (
    <ReportContent
      data={data}
      isAdmin={false}
      onBack={handleBack}
      inspectionId={effectiveInspectionId}
    />
  );
}
